import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CircleDot, Target, Lightbulb, XCircle, Anchor, ChevronRight, FileText } from 'lucide-react';
import { Reveal, EASE } from './helpers';

const DU_FIELDS = [
    {
        key: 'situation',
        icon: AlertCircle,
        label: 'What was going on',
        hint: 'The situation',
        body: "Acme's quarterly review fell flat. Usage was down 18% over the last 60 days, and the new procurement lead asked for a 25% discount to renew.",
    },
    {
        key: 'signals',
        icon: CircleDot,
        label: 'What they were looking at',
        hint: 'The signals that mattered',
        body: 'Usage trend over the last 60 days. Whether the original champion was still active. Whether the discount push came from procurement or from the actual user. Ticket volume from their team.',
    },
    {
        key: 'decision',
        icon: Target,
        label: 'What they did',
        hint: 'The call they made',
        body: 'Held off on the discount conversation. Booked a 30-minute usage walkthrough with the original champion before responding to procurement.',
    },
    {
        key: 'reasoning',
        icon: Lightbulb,
        label: 'Why they did it',
        hint: 'The thinking behind the call',
        body: 'Pushback at renewal is usually a procurement signal, not a value signal — the usage data tells you which one. Discounting first sets the bar lower for next year and every year after.',
    },
    {
        key: 'rejected',
        icon: XCircle,
        label: 'What they almost did',
        hint: 'The option they rejected',
        body: 'Offering a quick 15% discount to keep the deal warm. They turned it down — it would have set a precedent across the whole renewal book.',
    },
    {
        key: 'heuristic',
        icon: Anchor,
        label: 'The rule they live by',
        hint: 'The principle that emerged',
        body: "Always re-engage the original champion before any procurement conversation. The champion's usage data is your only honest signal.",
    },
] as const;

export const DecisionUnitUnboxer = () => {
    const [active, setActive] = useState<string>(DU_FIELDS[0].key);
    const [auto, setAuto] = useState(true);

    useEffect(() => {
        if (!auto) return;
        const t = setInterval(() => {
            setActive((cur) => {
                const i = DU_FIELDS.findIndex((f) => f.key === cur);
                return DU_FIELDS[(i + 1) % DU_FIELDS.length].key;
            });
        }, 3400);
        return () => clearInterval(t);
    }, [auto]);

    const current = DU_FIELDS.find((f) => f.key === active) ?? DU_FIELDS[0];

    return (
        <section id="capture" className="py-32 scroll-mt-16 border-t border-border/40 bg-muted/20">
            <div className="max-w-6xl mx-auto px-6">
                <Reveal className="max-w-3xl mb-14">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">What we capture</p>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                        Not what they did.
                        <span className="text-muted-foreground/60"> How they thought.</span>
                    </h2>
                    <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                        For every important call your expert has made, we reconstruct six things — the situation, the signals, the decision, the reasoning, the option they rejected, and the rule of
                        thumb underneath. Tap any field to see what we mean.
                    </p>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    <div className="lg:col-span-5 space-y-1.5">
                        {DU_FIELDS.map((f, i) => {
                            const isActive = f.key === active;
                            return (
                                <button
                                    key={f.key}
                                    onClick={() => {
                                        setActive(f.key);
                                        setAuto(false);
                                    }}
                                    aria-pressed={isActive}
                                    aria-label={`Show: ${f.label}`}
                                    className={`w-full text-left rounded-xl px-4 py-3.5 transition-all flex items-start gap-3 group ${
                                        isActive ? 'bg-card border border-primary/40 shadow-sm' : 'bg-transparent border border-transparent hover:bg-card/60'
                                    }`}
                                >
                                    <div
                                        className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                            isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                                        }`}
                                    >
                                        <f.icon className="h-4 w-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-mono tabular-nums ${isActive ? 'text-primary' : 'text-muted-foreground/60'}`}>0{i + 1}</span>
                                            <span className={`text-sm font-semibold ${isActive ? 'text-foreground' : 'text-foreground/80'}`}>{f.label}</span>
                                        </div>
                                        <div className={`text-xs mt-0.5 ${isActive ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>{f.hint}</div>
                                    </div>
                                    <ChevronRight
                                        className={`h-4 w-4 mt-1.5 transition-all ${
                                            isActive ? 'text-primary translate-x-0' : 'text-muted-foreground/40 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0'
                                        }`}
                                    />
                                </button>
                            );
                        })}
                    </div>

                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-border/60 bg-card p-8 min-h-[360px] relative overflow-hidden">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">From a real handover · Sarah Chen, Head of Customer Success</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    {DU_FIELDS.map((f) => (
                                        <div key={f.key} className={`h-1 rounded-full transition-all ${f.key === active ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/20'}`} />
                                    ))}
                                </div>
                            </div>

                            <AnimatePresence mode="wait">
                                <motion.div key={current.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.45, ease: EASE }}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <current.icon className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-semibold tracking-tight">{current.label}</div>
                                            <div className="text-xs text-muted-foreground">{current.hint}</div>
                                        </div>
                                    </div>
                                    <p className="text-[17px] text-foreground/85 leading-relaxed">{current.body}</p>
                                </motion.div>
                            </AnimatePresence>

                            <div className="absolute bottom-6 left-8 right-8 pt-4 border-t border-border/40 flex items-center justify-between text-[11px] text-muted-foreground">
                                <span>Captured in a 45-minute session · approved by the expert before it's saved</span>
                                <span className="font-mono text-muted-foreground/50">0{DU_FIELDS.findIndex((f) => f.key === active) + 1}/6</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
